import { Grid, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  GetTripQuery,
  ShipmentLocationType,
  useGetOrganizationSettingsQuery,
} from "../../../graphql/generated";
import {
  formatDateForLocation,
  formatDateTime,
  formatTimeForLocation,
} from "../../../utils/labels/formatDateTime";
import {
  EtaWindowStatus,
  getEtaWindowStatus,
  getRemainingTripShipmentLocations,
  isTripEligibleForLiveEta,
  TripForShipmentLocationUtils,
  TripShipmentLocationWithEta,
} from "../../../utils/trip/getEtaWindowStatus";
import InfoBlock from "../../common/InfoBlock";
import { testIds } from "./TripEtaSummary.testIds";

const UNAVAILABLE = "—";

const etaStatusColor: Record<EtaWindowStatus, string> = {
  green: "success.main",
  yellow: "warning.main",
  red: "error.main",
};

export type TripEtaSummaryProps = {
  trip: Exclude<GetTripQuery["tripById"], null | undefined>;
};

function formatEtaForLocation(
  estimatedArrivalTime: string | Date,
  location: TripShipmentLocationWithEta,
): string {
  const eta = new Date(estimatedArrivalTime);
  return `${formatDateForLocation(eta, location, false)}, ${formatTimeForLocation(eta, location)}`;
}

function getLastDropoffLocation(
  trip: TripForShipmentLocationUtils,
): TripShipmentLocationWithEta | undefined {
  const dropoffs = trip.shipmentLocations.filter(
    (location) => location.locationType === ShipmentLocationType.DropOff,
  );
  return dropoffs[dropoffs.length - 1];
}

type EtaFieldProps = {
  label: string;
  value: string;
  color?: string;
  testId: string;
};

const EtaField = ({ label, value, color, testId }: EtaFieldProps) => (
  <TextField
    label={label}
    value={value}
    size="small"
    fullWidth
    InputProps={{ readOnly: true }}
    InputLabelProps={{ shrink: true }}
    inputProps={
      {
        "data-testid": testId,
      } as React.InputHTMLAttributes<HTMLInputElement>
    }
    sx={{
      "& .MuiInputBase-input": {
        cursor: "default",
        ...(color ? { color } : {}),
      },
    }}
  />
);

const TripEtaSummary = ({ trip }: TripEtaSummaryProps) => {
  const { t } = useTranslation("trips");
  const orgSettingsQuery = useGetOrganizationSettingsQuery();
  const etaEnabled =
    !!orgSettingsQuery.data?.organizationSettings?.dispatching?.enableEta;

  const tripForUtils = trip as TripForShipmentLocationUtils & {
    liveEtaUpdatedAt?: string | Date | null;
  };

  const { nextStop, deliveryStop, hasAnyEta } = useMemo(() => {
    const next = getRemainingTripShipmentLocations(tripForUtils)[0];
    const delivery = getLastDropoffLocation(tripForUtils);
    const hasEta =
      Boolean(next?.estimatedArrivalTime) ||
      Boolean(delivery?.estimatedArrivalTime);
    return { nextStop: next, deliveryStop: delivery, hasAnyEta: hasEta };
  }, [tripForUtils]);

  if (!etaEnabled || !isTripEligibleForLiveEta(trip.status) || !hasAnyEta) {
    return null;
  }

  const nextStopValue = nextStop?.estimatedArrivalTime
    ? formatEtaForLocation(nextStop.estimatedArrivalTime, nextStop)
    : UNAVAILABLE;
  const nextStopStatus = nextStop
    ? getEtaWindowStatus(tripForUtils, nextStop)
    : null;

  const deliveryValue = deliveryStop?.estimatedArrivalTime
    ? formatEtaForLocation(deliveryStop.estimatedArrivalTime, deliveryStop)
    : UNAVAILABLE;
  const deliveryStatus = deliveryStop
    ? getEtaWindowStatus(tripForUtils, deliveryStop)
    : null;

  return (
    <InfoBlock title={t("etaSummary", "Live ETA")}>
      {trip.liveEtaUpdatedAt ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          {t("liveEtaUpdatedAt", "Updated at {{time}}", {
            time: formatDateTime(trip.liveEtaUpdatedAt, null, true),
          })}
        </Typography>
      ) : null}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <EtaField
            label={t("etaToNextStop", "ETA to next stop")}
            value={nextStopValue}
            color={nextStopStatus ? etaStatusColor[nextStopStatus] : undefined}
            testId={testIds.tripEtaSummaryNextStopInput}
          />
        </Grid>
        <Grid item xs={12}>
          <EtaField
            label={t("etaToDelivery", "ETA to delivery")}
            value={deliveryValue}
            color={
              deliveryStatus ? etaStatusColor[deliveryStatus] : undefined
            }
            testId={testIds.tripEtaSummaryDeliveryInput}
          />
        </Grid>
      </Grid>
    </InfoBlock>
  );
};

export default TripEtaSummary;
